import { useEffect, useState, type FormEvent } from 'react';
import QRCode from 'qrcode';
import { delay } from '@caios/ui';
import { authClient } from '../auth.ts';

/** Authenticator-app enrolment: confirm password → scan QR (or key) → confirm a code → save backup codes. */
export function TotpEnrol({ onDone, onCancel }: { onDone: () => void; onCancel?: () => void }) {
  const [step, setStep] = useState<'password' | 'scan' | 'backup'>('password');
  const [password, setPassword] = useState('');
  const [uri, setUri] = useState('');
  const [qr, setQr] = useState('');
  const [code, setCode] = useState('');
  const [backup, setBackup] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (uri) QRCode.toDataURL(uri, { margin: 1, width: 220 }).then(setQr); }, [uri]);
  const secret = uri ? new URL(uri).searchParams.get('secret') ?? '' : '';

  async function start(e: FormEvent) {
    e.preventDefault(); setBusy(true); setError(null);
    const r = await authClient.twoFactor.enable({ password });
    setBusy(false); setPassword('');
    if (r.error || !r.data || !('totpURI' in r.data)) return setError('That password didn’t match.');
    setUri(r.data.totpURI); setBackup(r.data.backupCodes); setStep('scan');
  }
  async function confirm(e: FormEvent) {
    e.preventDefault(); setBusy(true); setError(null);
    const r = await authClient.twoFactor.verifyTotp({ code: code.replace(/\s/g, '') });
    setBusy(false);
    if (r.error) return setError('That code didn’t work. Use the newest code your app shows.');
    setStep('backup');
  }

  return (<>
    {step === 'password' && (<form onSubmit={start} className="auth-form">
      <label className="field"><span className="cx-label">Confirm your password</span>
        <input type="password" autoComplete="current-password" autoFocus required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="setup-totp-password" /></label>
      <div className="btn-row">
        <button type="submit" className="cx-btn cx-btn--primary" disabled={busy}>Continue</button>
        {onCancel && <button type="button" className="cx-btn" onClick={onCancel}>Cancel</button>}
      </div>
    </form>)}
    {step === 'scan' && (<form onSubmit={confirm} className="auth-form">
      <h2 className="cx-h2">Scan this with your authenticator app</h2>
      {qr && <img src={qr} alt="QR code for your authenticator app" className="qr cx-rise" />}
      <p className="cx-label">Can’t scan? Enter this key: <code className="secret" data-testid="setup-totp-secret">{secret}</code></p>
      <label className="field"><span className="cx-label">Then type the 6-digit code it shows</span>
        <input inputMode="numeric" autoComplete="one-time-code" autoFocus required value={code} onChange={(e) => setCode(e.target.value)} className="code-input" data-testid="setup-totp-code" /></label>
      <button type="submit" className="cx-btn cx-btn--primary auth-wide" disabled={busy} data-testid="setup-totp-confirm">Confirm</button>
    </form>)}
    {step === 'backup' && <BackupCodes codes={backup} onDone={onDone} />}
    {error && <p className="auth-error" role="alert">{error}</p>}
  </>);
}

export function BackupCodes({ codes, onDone }: { codes: string[]; onDone: () => void }) {
  return (<>
    <h2 className="cx-h2">Save your backup codes</h2>
    <p className="cx-body">Each one works once, if you ever lose your phone. This is the only time they’re shown. Keep them in your password manager.</p>
    <ol className="backup-codes" data-testid="backup-codes">
      {codes.map((c, i) => <li key={c} className="cx-mono cx-rise" style={delay(i, 40)}>{c}</li>)}
    </ol>
    <div className="btn-row">
      <button type="button" className="cx-btn" onClick={() => navigator.clipboard?.writeText(codes.join('\n'))}>Copy codes</button>
      <button type="button" className="cx-btn cx-btn--primary" onClick={onDone} data-testid="backup-done">I’ve saved them</button>
    </div>
  </>);
}
